import { TableV3FilterItem } from 'common/table/types/TableV3';
import { minMaxToTableFilterItems } from 'common/table/utils/minMaxTableFilterItems';
import { rangeConditionToColumnMinMax } from 'modules/candidate-profile/tables/filters/rangeConditionColumnMinMax';
import {
  CandidateFilterMatchMode,
  CandidateFilterState,
  CandidateFilterValueType,
  CandidateRangeFilter,
  CandidateSelectionFilter,
  SourcingFilterField,
  RangeFilterSettings
} from 'modules/candidate-profile/types/CandidateSearchFilter';

export const DEFAULT_MATCH_MODE = CandidateFilterMatchMode.INCLUDE_ANY;

export type CandidateFilterActiveModes = Record<
  string,
  CandidateFilterMatchMode
>;

type SelectionTarget = {
  field: string;
  vectorDbKey?: string;
  type: CandidateFilterValueType.TEXT | CandidateFilterValueType.TEXT_ARRAY;
  mode: CandidateFilterMatchMode;
};

export const isSelection = (
  filter: CandidateFilterState
): filter is CandidateSelectionFilter =>
  filter.type === CandidateFilterValueType.TEXT ||
  filter.type === CandidateFilterValueType.TEXT_ARRAY;

export const isRange = (
  filter: CandidateFilterState
): filter is CandidateRangeFilter =>
  filter.type === CandidateFilterValueType.NUMBER ||
  filter.type === CandidateFilterValueType.DATE;

/** The same values, in any order. */
export const sameValues = (a: string[], b: string[]): boolean =>
  a.length === b.length && a.every((value) => b.includes(value));

export const settingsKey = (filter: CandidateFilterState): string => {
  const settings: Partial<CandidateFilterState> = { ...filter };
  delete settings.id;
  return JSON.stringify(settings, Object.keys(settings).sort());
};

const matches = (
  filter: CandidateFilterState,
  field: string,
  mode: CandidateFilterMatchMode
) => isSelection(filter) && filter.field === field && filter.mode === mode;

/**
 * Both halves of an AND-ed tree can constrain one field in one mode, and
 * `contains any of [a]` AND `contains any of [b]` is not `contains any of
 * [a, b]`. They stay two conditions, so the duplicate index tells them apart.
 */
export const conditionId = (
  field: string,
  mode: CandidateFilterMatchMode,
  duplicateIndex = 0
) =>
  duplicateIndex ? `${field}:${mode}#${duplicateIndex}` : `${field}:${mode}`;

export const freeId = (base: string, taken: Set<string>): string =>
  Array.from({ length: taken.size + 1 }, (_, ordinal) =>
    ordinal ? `${base}#${ordinal}` : base
  ).find((id) => !taken.has(id)) as string;

export const baseId = (filter: CandidateFilterState): string =>
  isSelection(filter) ? conditionId(filter.field, filter.mode) : filter.field;

// A condition keeps its id for life, so Or groups can name it.
export const withFreeIds = (
  incoming: CandidateFilterState[],
  existing: CandidateFilterState[]
): CandidateFilterState[] => {
  const taken = new Set(existing.map((filter) => filter.id));

  return incoming.map((filter) => {
    const id = taken.has(filter.id) ? freeId(baseId(filter), taken) : filter.id;
    taken.add(id);
    return id === filter.id ? filter : { ...filter, id };
  });
};

// The first condition on a field and mode is the column's; the rest are
// chips only.
export const columnCondition = (
  filters: CandidateFilterState[],
  field: string,
  mode: CandidateFilterMatchMode
): CandidateSelectionFilter | undefined =>
  filters.find((filter): filter is CandidateSelectionFilter =>
    matches(filter, field, mode)
  );

export const findRangeConditionShownInColumn = (
  filters: CandidateFilterState[],
  field: string
): CandidateRangeFilter | undefined =>
  filters.find(
    (filter): filter is CandidateRangeFilter =>
      isRange(filter) && filter.field === field
  );

export const isColumnCondition = (
  filters: CandidateFilterState[],
  filter: CandidateFilterState
): boolean =>
  isSelection(filter) &&
  columnCondition(filters, filter.field, filter.mode) === filter;

export const getActiveMode = (
  activeModes: CandidateFilterActiveModes,
  field: string
): CandidateFilterMatchMode => activeModes[field] ?? DEFAULT_MATCH_MODE;

export const getSelectedValues = (
  filters: CandidateFilterState[],
  field: string,
  mode: CandidateFilterMatchMode
): string[] => columnCondition(filters, field, mode)?.values ?? [];

/** Add or remove one value; the condition is dropped when its last value goes. */
export const toggleFilterValue = (
  filters: CandidateFilterState[],
  target: SelectionTarget,
  value: string
): CandidateFilterState[] => {
  const existing = columnCondition(filters, target.field, target.mode);

  if (!existing) {
    return [
      ...filters,
      {
        id: conditionId(target.field, target.mode),
        field: target.field,
        ...(target.vectorDbKey ? { vectorDbKey: target.vectorDbKey } : {}),
        type: target.type,
        mode: target.mode,
        values: [value]
      }
    ];
  }

  const values = existing.values.includes(value)
    ? existing.values.filter((v) => v !== value)
    : [...existing.values, value];

  if (!values.length) {
    return filters.filter((f) => f !== existing);
  }

  return filters.map((f) => (f === existing ? { ...existing, values } : f));
};

/** The condition a field's picked items make under one mode, or null for none. */
export const buildSelectionCondition = (
  available: SourcingFilterField,
  mode: CandidateFilterMatchMode,
  items: TableV3FilterItem[]
): CandidateSelectionFilter | null => {
  if (!items.length) return null;

  const labelled = items.filter((item) => item.label !== item.value);

  return {
    id: conditionId(available.field, mode),
    field: available.field,
    ...(available.vectorDbKey ? { vectorDbKey: available.vectorDbKey } : {}),
    type: available.type as
      | CandidateFilterValueType.TEXT
      | CandidateFilterValueType.TEXT_ARRAY,
    mode,
    values: items.map((item) => item.value),
    ...(labelled.length
      ? {
          valueLabels: Object.fromEntries(
            labelled.map((item) => [item.value, item.label])
          )
        }
      : {})
  };
};

/** Replace whatever one field held under one mode. */
export const setModeCondition = (
  filters: CandidateFilterState[],
  available: SourcingFilterField,
  mode: CandidateFilterMatchMode,
  items: TableV3FilterItem[]
): CandidateFilterState[] => {
  const replaced = columnCondition(filters, available.field, mode);
  const others = filters.filter((filter) => filter !== replaced);
  const condition = buildSelectionCondition(available, mode, items);

  return condition
    ? [...others, { ...condition, id: replaced?.id ?? condition.id }]
    : others;
};

export const removeCondition = (
  filters: CandidateFilterState[],
  field: string,
  mode: CandidateFilterMatchMode,
  id?: string
): CandidateFilterState[] =>
  id
    ? filters.filter((f) => f.id !== id)
    : filters.filter((f) => !matches(f, field, mode));

export const clearAllFilters = (): CandidateFilterState[] => [];

/**
 * Project onto TableV3's shape. It holds one entry per column, so only the
 * active mode is shown; the condition list stays the source of truth.
 */
export const toTableFilterValues = (
  filters: CandidateFilterState[],
  activeModes: CandidateFilterActiveModes,
  {
    labelFor = (_, value) => value,
    rangeFor = () => undefined
  }: {
    labelFor?: (field: string, value: string) => string;
    rangeFor?: (field: string) => RangeFilterSettings | undefined;
  } = {}
): Record<string, TableV3FilterItem[]> =>
  filters.reduce<Record<string, TableV3FilterItem[]>>((acc, filter) => {
    if (filter.type === CandidateFilterValueType.BOOLEAN) {
      // The column holds one value; the first stands.
      if (acc[filter.field]) return acc;

      return {
        ...acc,
        [filter.field]: [
          {
            value: String(filter.value),
            label: filter.value ? 'Yes' : 'No'
          }
        ]
      };
    }

    if (isRange(filter)) {
      if (findRangeConditionShownInColumn(filters, filter.field) !== filter)
        return acc;
      const range = rangeFor(filter.field);
      if (!range) return acc;

      return {
        ...acc,
        [filter.field]: minMaxToTableFilterItems(
          rangeConditionToColumnMinMax(filter, range)
        )
      };
    }

    if (!isSelection(filter)) return acc;
    if (filter.mode !== getActiveMode(activeModes, filter.field)) return acc;
    if (!isColumnCondition(filters, filter)) return acc;

    return {
      ...acc,
      [filter.field]: filter.values.map((value) => ({
        value,
        label: filter.valueLabels?.[value] ?? labelFor(filter.field, value)
      }))
    };
  }, {});
